
import { useState } from 'react';
import Navbar from '../../components/Navbar';

export default function DuesPage() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  const handlePayment = async () => {
    setIsProcessing(true);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-platform-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            priceId: 'price_monthly_dues',
            successUrl: `${window.location.origin}/dues?success=true`,
            cancelUrl: `${window.location.origin}/dues?canceled=true`,
          }),
        }
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      setIsProcessing(false);
      setNotificationMessage('❌ Payment setup failed. Please try again.');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    }
  };

  const paymentHistory = [
    { id: 1, date: '2025-01-01', amount: 150, status: 'paid', method: 'Visa ****4242' },
    { id: 2, date: '2024-12-01', amount: 150, status: 'paid', method: 'Visa ****4242' },
    { id: 3, date: '2024-11-01', amount: 150, status: 'paid', method: 'Visa ****4242' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      <Navbar />

      {/* Notification */}
      {showNotification && (
        <div className="fixed top-24 right-6 z-50 animate-[slideInRight_0.3s_ease-out]">
          <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <i className="ri-error-warning-fill text-2xl"></i>
            <span className="font-medium">{notificationMessage}</span>
          </div>
        </div>
      )}

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="mb-12 animate-[fadeIn_0.5s_ease-out]">
            <h1 className="text-5xl font-bold text-slate-900 mb-4">Monthly Dues</h1>
            <p className="text-xl text-slate-600">Manage your HOA monthly payments</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Payment Card */}
            <div className="lg:col-span-2 animate-[slideUp_0.5s_ease-out]">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-emerald-100 mb-2">Monthly Dues</p>
                      <h2 className="text-5xl font-bold">$150</h2>
                    </div>
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                      <i className="ri-home-heart-line text-4xl"></i>
                    </div>
                  </div>
                  <p className="text-emerald-50">Due on the 1st of each month</p>
                </div>

                <div className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">What's Included</h3>
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {[
                      { icon: 'ri-plant-line', text: 'Landscaping & Maintenance' },
                      { icon: 'ri-water-flash-line', text: 'Pool & Spa Upkeep' },
                      { icon: 'ri-shield-check-line', text: 'Security Services' },
                      { icon: 'ri-lightbulb-line', text: 'Common Area Utilities' },
                      { icon: 'ri-tools-line', text: 'Repairs & Improvements' },
                      { icon: 'ri-file-shield-line', text: 'Insurance Coverage' },
                    ].map((item, index) => (
                      <div 
                        key={index} 
                        className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-emerald-50 transition-all duration-200 animate-[fadeIn_0.5s_ease-out]"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <i className={`${item.icon} text-2xl text-emerald-500`}></i>
                        <span className="text-slate-700 font-medium">{item.text}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handlePayment}
                    disabled={isProcessing}
                    className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-200 flex items-center justify-center gap-3 whitespace-nowrap ${
                      isProcessing
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-emerald-500 text-white hover:bg-emerald-600 hover:scale-[1.02] shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {isProcessing ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-2xl"></i>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ri-secure-payment-line text-2xl"></i>
                        Set Up Automatic Payment
                      </>
                    )}
                  </button>

                  <p className="text-center text-slate-500 mt-4 text-sm">
                    <i className="ri-lock-line"></i> Secure payment powered by Stripe
                  </p>
                </div>
              </div>
            </div>

            {/* Payment History */}
            <div className="animate-[slideUp_0.5s_ease-out]" style={{ animationDelay: '0.2s' }}>
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-history-line text-emerald-500"></i>
                  Payment History
                </h3>
                <div className="space-y-4">
                  {paymentHistory.map((payment, index) => (
                    <div 
                      key={payment.id} 
                      className="p-4 bg-slate-50 rounded-lg hover:bg-emerald-50 transition-all duration-200 animate-[fadeIn_0.5s_ease-out]"
                      style={{ animationDelay: `${(index + 6) * 0.1}s` }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">${payment.amount}</span>
                        <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                          <i className="ri-checkbox-circle-fill"></i>
                          {payment.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{new Date(payment.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      <p className="text-sm text-slate-500 mt-1">{payment.method}</p>
                    </div>
                  ))}
                </div>

                <button className="w-full mt-6 py-3 border-2 border-slate-200 text-slate-700 rounded-lg font-semibold hover:border-emerald-500 hover:text-emerald-500 transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap">
                  <i className="ri-file-list-line"></i>
                  View All Payments
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
